/**
 * A BAG ITEM ON THE FAMILY'S TO-DO LIST (bag group plan v2, BG-8; Bobbie,
 * 2026-08-08: assigned items appear on the family's home to-dos). The server
 * half, and the whole story, is netlify/functions/lib/bagGroupItemTodo.mjs:
 * the item lane writes one to-do per item, in the same transaction as the
 * claim or the assign, at a fixed id, so it can never be written twice.
 *
 * Firebase-free on purpose: the To-dos page, the server and the plain node
 * tests all import this.
 */

/** Every bag item to-do id starts with this. */
export const BAG_ITEM_TASK_PREFIX = 'bagItem_';
/** The one button a bag item to-do carries to its group. */
export const BAG_ITEM_OPEN_LABEL = 'Open bag group';
/** Said on the card so nobody hunts for a Mark done that proves nothing. */
export const BAG_ITEM_SELF_CLEARS =
  'Marking it dropped off on the bag group page checks this off.';

/**
 * The one to-do id for one item: keyed by the item, never by a page load.
 *
 * The group id's length goes first, so the id can be split back into exactly
 * one (groupId, itemId) pair. A plain join could not: '_' is a legal id
 * character (validDocId), so group "a_b" with item "c" and group "a" with
 * item "b_c" would both have been bagItem_a_b_c (refute round 1 on BG-8,
 * minor 6). The planner also refuses to touch a to-do at this id whose own
 * bagGroupId and bagItemId say it belongs to another item.
 */
export function bagItemTaskId(groupId: string, itemId: string): string {
  return `${BAG_ITEM_TASK_PREFIX}${groupId.length}_${groupId}_${itemId}`;
}

/** The group page, opened at the family's own list. */
export function bagItemGroupHref(groupId: string): string {
  return `/bag-groups/${encodeURIComponent(groupId)}#owe`;
}

/**
 * The group a bag item to-do is about, or null for every other to-do. Both
 * halves must agree: the server-owned fields AND the id built from them, so a
 * hand-typed field on some other to-do never turns it into one of these.
 */
export function bagItemTaskGroupId(task: {
  id: string;
  bagGroupId?: string | null;
  bagItemId?: string | null;
}): string | null {
  const groupId = typeof task.bagGroupId === 'string' ? task.bagGroupId : '';
  const itemId = typeof task.bagItemId === 'string' ? task.bagItemId : '';
  if (!groupId || !itemId) return null;
  return task.id === bagItemTaskId(groupId, itemId) ? groupId : null;
}

/**
 * BELT AND BRACES FOR A CLOSED GROUP (BG-8, refute round 1, major 1). The
 * server deletes a group's to-dos when it is archived or a family leaves it
 * (netlify/functions/lib/bagGroupTodoSweep.mjs and the family lane), but that
 * runs after the change and can fail. Until it lands, a bag item to-do whose
 * group the viewer can no longer open is left off the list rather than shown
 * with an Open bag group button that leads nowhere.
 *
 * liveGroupIds: the active groups this viewer can open, or null while that is
 * still unknown (loading, or the read failed), when nothing is hidden: a
 * to-do that flickers away is worse than one that stays a moment too long.
 */
export function withoutClosedBagItemTasks<T extends { id: string; bagGroupId?: string | null; bagItemId?: string | null }>(
  tasks: readonly T[],
  liveGroupIds: ReadonlySet<string> | null
): T[] {
  if (!liveGroupIds) return [...tasks];
  return tasks.filter((task) => {
    const groupId = bagItemTaskGroupId(task);
    return groupId === null || liveGroupIds.has(groupId);
  });
}
